import React from "react";
import { View, StyleSheet } from "react-native";
import AppNavigator from "../../navigation/AppNavigator";
import routes from "../../navigation/routes";
import Logger from "../../common/utils/Logger";
import RootController from "./RootController";
import { navigationRef } from "../../../App";
import settings from "../../config/settings";
import NtAppShell from "../components/NtAppShell";
import NtSidebar from "../components/NtSidebar";
import { getPreferences } from "../../config/preferences";

class RootAppController extends RootController {
  state = {
    currentController: null,
    isMobile: false,
    sidebarCollapsed: this.context.user?.ui
      ? this.context.user?.ui?.sidebarcollapsed
      : true,
  };

  componentDidMount() {
    super.componentDidMount();
  }

  /**
   * Flatten the permission-gated nav into the shape NtSidebar consumes:
   * [ { type: 'label', text }, { type: 'item', key, label, icon, onPress, isActive, visible }, ... ]
   *
   * Broadcast + Messaging are intentionally excluded from the new sidebar
   * (functionality stays in the app but nav entry points are removed).
   */
  buildSidebarItems = () => {
    const nav = (route, params) => () => navigationRef.current.navigate(route, params);
    const isOn = (route, paramsTypeEquals) => () => {
      const cur = navigationRef.current?.getCurrentRoute();
      if (cur?.name !== route) return false;
      if (paramsTypeEquals !== undefined) return cur?.params?.type === paramsTypeEquals;
      return true;
    };

    const items = [];

    // Operations
    items.push({ type: "label", text: "Operations" });
    items.push({
      type: "item", key: "dashboard", label: "Dashboard", icon: "gauge",
      visible: true, onPress: nav(routes.dashboard), isActive: isOn(routes.dashboard),
    });
    items.push({
      type: "item",
      key: "setup",
      label: "Setup Wizard",
      icon: "rocket-launch-outline",
      badge: "NEW",
      flashy: true,
      visible: true,
      onPress: nav(routes.setup),
      isActive: isOn(routes.setup),
    });

    // Calling Cards
    const cc = [
      { key: "pin.cabins", label: "PIN · Cabins", icon: "pin",
        visible: this.handlePermissionCheck(settings.permissionTypeView, `${routes.pinmanagement}/cabin`),
        onPress: nav(routes.pinmanagement, { type: "cabin" }),
        isActive: isOn(routes.pinmanagement, "cabin") },
      { key: "pin.admins", label: "PIN · Admins", icon: "pin",
        visible: this.handlePermissionCheck(settings.permissionTypeView, `${routes.pinmanagement}/admin`),
        onPress: nav(routes.pinmanagement, { type: "admin" }),
        isActive: isOn(routes.pinmanagement, "admin") },
      { key: "cdr.cabins", label: "CDR · Cabins", icon: "history",
        visible: this.handlePermissionCheck(settings.permissionTypeView, `${routes.pincdr}/cabin`),
        onPress: nav(routes.pincdr, { type: "cabin" }),
        isActive: isOn(routes.pincdr, "cabin") },
      { key: "cdr.admins", label: "CDR · Admins", icon: "history",
        visible: this.handlePermissionCheck(settings.permissionTypeView, `${routes.pincdr}/admin`),
        onPress: nav(routes.pincdr, { type: "admin" }),
        isActive: isOn(routes.pincdr, "admin") },
      { key: "callingrate", label: "Calling Rates", icon: "chart-bar",
        visible: this.handlePermissionCheck(settings.permissionTypeView, routes.callingrate),
        onPress: nav(routes.callingrate), isActive: isOn(routes.callingrate) },
      { key: "reportschedule", label: "Report Schedule", icon: "archive",
        visible: this.handlePermissionCheck(settings.permissionTypeView, routes.reportschedule),
        onPress: nav(routes.reportschedule), isActive: isOn(routes.reportschedule) },
    ].filter((i) => i.visible);
    // Whole calling-card section is gated behind the runtime feature flag.
    if (settings.callingCardsEnabled && cc.length) {
      items.push({ type: "label", text: "Calling Cards" });
      cc.forEach((i) => items.push({ type: "item", ...i }));
    }

    // PBX
    const pbx = [
      { key: "registrations", label: "Registrations", icon: "lan-connect",
        visible: this.handlePermissionCheck(settings.permissionTypeView, routes.registration),
        onPress: nav(routes.registration), isActive: isOn(routes.registration) },
      { key: "extensions", label: "Extensions", icon: "phone",
        visible: this.handlePermissionCheck(settings.permissionTypeView, routes.extension),
        onPress: nav(routes.extension), isActive: isOn(routes.extension) },
      { key: "ringgroups", label: "Ring Groups", icon: "account-group",
        visible: this.handlePermissionCheck(settings.permissionTypeView, routes.ringgroup),
        onPress: nav(routes.ringgroup), isActive: isOn(routes.ringgroup) },
      { key: "numbers", label: "Numbers", icon: "phone-plus-outline",
        visible: true,
        onPress: nav(routes.number), isActive: isOn(routes.number) },
      { key: "inbound", label: "Inbound Route", icon: "phone-incoming",
        visible: this.handlePermissionCheck(settings.permissionTypeView, routes.inboundroute),
        onPress: nav(routes.inboundroute), isActive: isOn(routes.inboundroute) },
      { key: "ivr", label: "IVR Menus", icon: "menu",
        visible: this.handlePermissionCheck(settings.permissionTypeView, routes.ivr) || true,
        onPress: nav(routes.ivr), isActive: isOn(routes.ivr) },
      { key: "dialplan", label: "Dialplans", icon: "file-tree",
        visible: this.handlePermissionCheck(settings.permissionTypeView, routes.dialplan) || true,
        onPress: nav(routes.dialplan), isActive: isOn(routes.dialplan) },
      { key: "recording", label: "Recordings", icon: "microphone",
        visible: this.handlePermissionCheck(settings.permissionTypeView, routes.recording) || true,
        onPress: nav(routes.recording), isActive: isOn(routes.recording) },
      { key: "voicemail", label: "Voicemail", icon: "voicemail",
        visible: this.handlePermissionCheck(settings.permissionTypeView, routes.voicemail),
        onPress: nav(routes.voicemail), isActive: isOn(routes.voicemail) },
      { key: "hardware", label: "Hardware", icon: "chip",
        visible: this.handlePermissionCheck(settings.permissionTypeView, routes.hardware),
        onPress: nav(routes.hardware), isActive: isOn(routes.hardware) },
      { key: "featurecode", label: "Feature Codes", icon: "asterisk",
        visible: true,
        onPress: nav(routes.featurecode), isActive: isOn(routes.featurecode) },
    ].filter((i) => i.visible);
    if (pbx.length) {
      items.push({ type: "label", text: "PBX" });
      pbx.forEach((i) => items.push({ type: "item", ...i }));
    }

    // People
    const people = [
      { key: "user", label: "Users", icon: "account-circle",
        visible: this.handlePermissionCheck(settings.permissionTypeView, routes.user),
        onPress: nav(routes.user),
        isActive: () => {
          const cur = navigationRef.current?.getCurrentRoute()?.name;
          return cur === routes.user || cur === routes.userdetails;
        } },
    ].filter((i) => i.visible);
    if (people.length) {
      items.push({ type: "label", text: "People" });
      people.forEach((i) => items.push({ type: "item", ...i }));
    }

    // Account — settings is always available to a signed-in user.
    items.push({ type: "label", text: "Account" });
    items.push({
      type: "item",
      key: "settings",
      label: "Settings",
      icon: "cog-outline",
      visible: true,
      onPress: nav(routes.settings),
      isActive: isOn(routes.settings),
    });

    return items;
  };

  showSidePannel = () => {
    return (
      this.state.currentController !== routes.notfound &&
      this.state.currentController !== routes.marketing &&
      this.state.currentController !== routes.health &&
      this.state.currentController !== routes.guestapp &&
      this.state.currentController !== routes.crewapp &&
      this.state.currentController !== routes.hospitality &&
      this.state.currentController !== routes.impactstudy &&
      this.state.currentController !== routes.setup
    );
  };

  render() {
    const showShell = this.showSidePannel();
    const landingRoute = getPreferences().landingRoute;
    const navigator = (
      <AppNavigator
        initialRoute={landingRoute}
        onControllerChanged={(controller) => {
          Logger("Current controller: ", controller);
          this.setState({ currentController: controller });
        }}
      />
    );

    // Marketing / not-found / auxiliary routes: no shell, full-bleed page.
    if (!showShell) {
      return <View style={styles.container}>{navigator}</View>;
    }

    return (
      <View style={styles.container}>
        <NtAppShell
          sidebar={<NtSidebar items={this.buildSidebarItems()} />}
        >
          {navigator}
        </NtAppShell>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    padding: 0,
    flex: 1,
  },
});

export default RootAppController;
